import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { LockedFeature } from "@/components/LockedFeature";
import { ArrowLeft, Loader2, Lightbulb, AlertTriangle, Rocket, Shield, TrendingUp } from "lucide-react";

export default function CaseStudyDetail() {
  const { studyId } = useParams();
  const [s, setS] = useState(null);
  const [locked, setLocked] = useState(false);
  useEffect(() => {
    api.get(`/case-studies/${studyId}`).then((r) => setS(r.data))
      .catch((e) => { if (e.response?.status === 402) setLocked(true); });
  }, [studyId]);

  if (locked) return (
    <LockedFeature title="Case Study Library" description="Detailed company breakdowns are part of ECO Pro — business models, growth strategies, key decisions and mistakes."
      perks={["15+ legendary companies", "Generate any company on demand", "Actionable insights you can apply"]} />
  );

  if (!s) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-600" /></div>;

  return (
    <div className="space-y-8 max-w-4xl" data-testid="case-study-detail">
      <Link to="/case-studies" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white"><ArrowLeft size={16} /> Case Studies</Link>

      <div>
        <div className="flex items-center gap-3">
          <h1 className="font-display text-4xl font-bold text-white tracking-tight">{s.company}</h1>
          <span className="eco-tag">{s.founded}</span>
        </div>
        <p className="text-slate-400 mt-2 text-lg">{s.tagline}</p>
        <span className="text-sm text-slate-500">{s.industry}</span>
      </div>

      <Text icon={Rocket} title="Business model" body={s.businessModel} color="#0055FF" />
      <Text icon={TrendingUp} title="Growth strategy" body={s.growthStrategy} color="#10B981" />
      <Text icon={Shield} title="Competitive advantage" body={s.competitiveAdvantage} color="#A855F7" />

      <div className="grid md:grid-cols-2 gap-5">
        <List icon={Lightbulb} title="Lessons learned" items={s.lessonsLearned} color="#F59E0B" />
        <List icon={AlertTriangle} title="Mistakes made" items={s.mistakesMade} color="#EF4444" />
      </div>

      <div className="eco-card p-6">
        <div className="font-mono-eco text-[10px] uppercase tracking-widest text-slate-500 mb-2">Key metrics</div>
        <p className="text-white">{s.keyMetrics}</p>
      </div>
    </div>
  );
}

function Text({ icon: I, title, body, color }) {
  return (
    <div className="eco-card p-6">
      <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-white mb-2"><I size={18} style={{ color }} /> {title}</h3>
      <p className="text-slate-300 leading-relaxed">{body}</p>
    </div>
  );
}
function List({ icon: I, title, items, color }) {
  return (
    <div className="eco-card p-6">
      <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-white mb-4"><I size={18} style={{ color }} /> {title}</h3>
      <ul className="space-y-2.5">
        {items.map((it, i) => <li key={i} className="flex items-start gap-3 text-sm text-slate-300"><span className="h-1.5 w-1.5 rounded-full mt-2 shrink-0" style={{ background: color }} /> {it}</li>)}
      </ul>
    </div>
  );
}
